import { useMutation, useQueryClient } from '@tanstack/react-query';

declare module '@tanstack/react-query' {
  interface Register {
    /**
     * `success` is toasted by the mutation cache in `main.tsx`. Failures need
     * no meta: every failed query and mutation is toasted there already.
     */
    mutationMeta: { success?: string };
  }
}

/**
 * A mutation that invalidates one key namespace on success.
 *
 * The keys are coarse on purpose: a wallet edit changes the finances overview's
 * waterfall too, so a mutation invalidates its whole namespace rather than
 * trying to be clever about which views moved.
 */
export function useInvalidating<TArgs, TResult>(
  queryKey: readonly unknown[],
  mutationFn: (args: TArgs) => Promise<TResult>,
  success?: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    meta: { success },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });
}
